import { getProject } from '@lib/api_ts/projectApi';
import { useQuery } from '@tanstack/react-query';
import { getCoordinatorByProjectId } from 'lib/api_ts/coordinatorApi';
import { getPartnersByCompanyId } from 'lib/api_ts/parternshipApi';
import { createContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

export type PermissionType = {
	name: string;
	labelIt: string;
	labelEn?: string;
	descriptionIt?: string;
	descriptionEn?: string;
	category?: string;
};

// Tipi per l'utente e per il contesto
type UserType = {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	isOe?: boolean;
	roles?: number[];
	companies?: number[]; // Aggiunto per rappresentare le aziende associate all'utente
	permissions?: PermissionType[];
};

type UserAccessType = {
	isSuperAdmin: boolean;
	isAdmin: boolean;
	isOeExternal: boolean;
	isPartnerExtended: boolean;
	isAbovePartner: boolean;
	isPartner: boolean;
	isAboveGuest: boolean;
	isGuest: boolean;
	isAboveAuditor: boolean;
	isAuditor: boolean;
	isGuestEnteErogante: boolean;
};

type UserContextType = {
	myUser: UserType | null;
	isCoordinator: boolean | undefined;
	isBeneficiary: boolean | undefined;
	isOe: boolean | undefined;
	userAccess: UserAccessType;
	loginContext: (userData: UserType) => void;
	logoutContext: () => void;
	loadingContext: boolean;
};

export const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
	const location = useLocation();
	const projectId = location.pathname.split('/').at(2);
	const [myUser, setMyUser] = useState<UserType>(null);
	const [loadingContext, setLoadingContext] = useState(true);
	const [isCoordinator, setIsCoordinator] = useState<boolean>();
	const [partnerIds, setPartnerIds] = useState([]);
	const isBeneficiary = useMemo(
		() => (partnerIds.length && myUser?.companies ? myUser.companies.some((element: number) => partnerIds.includes(element)) : undefined),
		[partnerIds, myUser?.companies],
	);

	const isOe = useMemo(() => myUser?.isOe, [myUser]);

	const userAccess = useMemo(() => {
		const role = myUser?.roles?.length ? Math.min(...myUser.roles) : 70;

		return {
			isSuperAdmin: role === 1,
			isAdmin: role <= 2,
			isExternalAdmin: role <= 4,
			isOeExternal: role <= 30,
			isAboveIncludingProjectManager: role <= 31,
			isProjectManager: role === 31,
			isPartnerExtended: role <= 39,
			isAbovePartner: role < 40,
			isPartner: role <= 40,
			isAboveGuest: role < 50,
			isGuest: role <= 50,
			isAboveAuditor: role < 70,
			isAuditor: role <= 70,
			isGuestEnteErogante: role === 71,
		};
	}, [myUser]);

	const { data: projectInfo, isPending: isProjectInfoPending } = useQuery({
		queryKey: ['project', projectId],
		queryFn: async () => getProject(projectId),
		enabled: projectId !== undefined && !!projectId,
	});

	const { data: partnersFromApi, isPending: isPartnersPending } = useQuery({
		queryKey: ['partners', projectInfo?.companyId],
		initialData: [],
		queryFn: async () => getPartnersByCompanyId(`${projectInfo.companyId}`),
		enabled: !!projectInfo?.companyId,
	});

	useEffect(() => {
		if (!projectId || isProjectInfoPending || isPartnersPending) {
			setIsCoordinator(undefined);
			return;
		}

		(async () => {
			try {
				// console.log('fetch project from UserProvider')
				// const projectInfo = await getProject(projectId);
				// const partnersFromApi = await getPartnersByCompanyId(projectInfo.data.companyId);
				setPartnerIds(partnersFromApi.map((partner) => partner.id));
			} catch (error) {
				console.error('Error fetching partners:', error);
				setPartnerIds([]);
			}
			try {
				const coordinatorResp = await getCoordinatorByProjectId(projectId);
				setIsCoordinator(coordinatorResp.isCoordinator);
			} catch (error) {
				console.error('Error fetching coordinator info:', error);
				setIsCoordinator(undefined);
			}
		})();
	}, [/*projectId*/ projectId, partnersFromApi, projectInfo?.companyId, isProjectInfoPending, isPartnersPending]);

	//Recupera i dati dell'utente da sessionStorage al primo render
	useEffect(() => {
		const storedUser = sessionStorage.getItem('myUser');

		if (storedUser) {
			try {
				const parsedUser = JSON.parse(storedUser);
				setMyUser(parsedUser);
			} catch (error) {
				console.error('Errore nel parsing di sessionStorage:', error);
			}
		}
		setLoadingContext(false);
	}, []);

	const loginContext = (userData) => {
		setMyUser(userData);
		sessionStorage.setItem('myUser', JSON.stringify(userData));
	};

	const logoutContext = () => {
		setMyUser(null);
		sessionStorage.removeItem('myUser');
	};

	return (
		<UserContext.Provider
			value={{
				myUser,
				isOe,
				isCoordinator,
				isBeneficiary,
				userAccess,
				loginContext,
				logoutContext,
				loadingContext,
			}}
		>
			{loadingContext ? <p>Caricamento...</p> : children}
		</UserContext.Provider>
	);
};
